import {useLazyQuery} from '@apollo/client'
import {GET_MARKET_PLACE_2} from '../../gql/queries/leadsQuery'
import {useEffect, useRef, useState} from 'react'
import {toast} from 'react-toastify'
import {FiltersPlans, IResultsJson, Plan} from '../oneLead/oneleadTypes'
import {useParams} from 'react-router-dom'
import {getYearFromEffectiveDate, removeEmptyKeys, removeEmptyKeysDeep, validateJsonQuote} from '../../helpers'
import {AccordionForm} from './components/AccordionForm'
import {IForm, IPerson} from './oneQuoteTypes'
import {ResultsCards} from './components/ResultsCards'
import {LoadingOneAddQuote} from './components/LoadingOneAddQuote'
import {SIZE_PAGE_QUICK_QUOTE} from '../../helpers/consts'
import {Button, Spinner} from 'react-bootstrap'
import {IResTest} from '../../types/leads'
import {useDisclourse} from '../../hooks/useDisclourse'
import {ModalAddPolice} from '../policies/components/ModalAddPolice'
import {set} from 'date-fns'

// Agrega props necesarias
interface OneAddQuoteProps {
  onClose?: () => void // Si necesitas un botón para cerrar el Drawer
  quoteData?: IForm
  onSelectPlan?: (plan: any) => void
}

export const OneAddQuotePage: React.FC<OneAddQuoteProps> = ({quoteData, onClose, onSelectPlan}) => {
  const [orderQuery, setOrderQuery] = useState('asc')

  const [gqlGetMarket, {data, loading: loadingPlans, error}] = useLazyQuery<IResTest>(
    GET_MARKET_PLACE_2,
    {
      fetchPolicy: 'no-cache',
    }
  )

  const {id} = useParams()

  const [formData, setFormData] = useState({} as IForm)
  const [arrPeople, setArrPeople] = useState<IPerson[]>([])

  const hasSetInitialRanges = useRef(false)
  const initialFacets = useRef<IResultsJson['facet_groups'] | null>(null)
  const initialRanges = useRef<{
    premiums: {min: number; max: number}
    deductibles: {min: number; max: number}
  } | null>(null)

  const [infoEnrollement, setInfoEnrollement] = useState({
    id: null as null | number,
    effectiveDate: '',
    planId: '',
    usesTobacco: false,
  })

  const [errorData, setErrorData] = useState(false)
  const [planForQuote, setPlanForQuote] = useState<Plan | undefined>(undefined)
  const [result, setResult] = useState<null | IResultsJson>(null)

  const [currentPage, setCurrentPage] = useState(1)
  const {isOpen: isOpenQuote, onClose: onCloseQuote, onOpen: onOpenQuote} = useDisclourse()

  const [filtersPlans, setFiltersPlans] = useState<FiltersPlans>({
    order: orderQuery ?? 'asc',
  })

  const [isUpdatingFilters, setIsUpdatingFilters] = useState(false) // Estado para bloquear la actualización de los filtros

  const handleFilters = () => {
    console.log('filtersPlans', filtersPlans)
    setIsUpdatingFilters(true) // Bloquear la actualización de los filtros

    const filters = {...filtersPlans}

    // Validar rangos
    if (
      initialRanges.current?.premiums &&
      filters.premiumValue !== undefined &&
      filters.premiumValue >= initialRanges.current.premiums.max
    ) {
      delete filters.premiumValue
    }

    if (
      initialRanges.current?.deductibles &&
      filters.deductibleValue !== undefined &&
      filters.deductibleValue >= initialRanges.current.deductibles.max
    ) {
      delete filters.deductibleValue
    }

    // Validar checkboxes
    if (!filters.selectedCarriers?.length) {
      delete filters.selectedCarriers
    }

    if (!filters.selectedMetalLevels?.length) {
      delete filters.selectedMetalLevels
    }

    if (!filters.selectedNetworkTypes?.length) {
      delete filters.selectedNetworkTypes
    }

    if (!filters.selectedDiseaseMgmtPrograms?.length) {
      delete filters.selectedDiseaseMgmtPrograms
    }
    // Validar booleanos
    if (!filters.childDentalCoverage) {
      delete filters.childDentalCoverage
    }
    if (!filters.adultDentalCoverage) {
      delete filters.adultDentalCoverage
    }
    if (!filters.hsaEligible) {
      delete filters.hsaEligible
    }
    if (!filters.simpleChoice) {
      delete filters.simpleChoice
    }
    // Actualizar los grupos filtrados
    const activeGroups: string[] = []
    if (filters.selectedCarriers) activeGroups.push('issuers')
    if (filters.selectedMetalLevels) activeGroups.push('metalLevels')
    if (filters.selectedNetworkTypes) activeGroups.push('types')
    if (filters.selectedDiseaseMgmtPrograms) activeGroups.push('diseaseMgmtPrograms')
    setFilteredGroups(activeGroups)

    handleQuery(formData, 0, filters)
  }

  const handleQuery = (queryData: IForm, offset: number = 0, filtersPlans?: FiltersPlans) => {
    try {
      const effectiveYear = getYearFromEffectiveDate(queryData?.effectiveDate)
      const arrPe = [
        {
          ...queryData?.personOne,
          age: Number(queryData.personOne.age),
          pregnantWith: queryData.personOne.pregnantWith
            ? Number(queryData.personOne.pregnantWith)
            : 0,
          currentEnrollment: queryData.personOne.currentEnrollment
            ? {
                ...queryData.personOne.currentEnrollment,
                id: undefined,
              }
            : null,
        },
        ...((queryData.people ?? []).map((inf) => ({
          ...inf,
          id: undefined,
          age: Number(inf.age),
          pregnantWith: inf.pregnantWith ? Number(inf.pregnantWith) : 0,
          currentEnrollment: inf.currentEnrollment
            ? {
                effectiveDate: inf.currentEnrollment.effectiveDate,
                planId: inf.currentEnrollment.planId,
                useTobacco: inf.currentEnrollment.useTobacco,
              }
            : null,
        })) || []),
      ]

      gqlGetMarket({
        variables: {
          ...queryData,
          year: effectiveYear,
          householdIncome: Number(queryData.householdIncome.replaceAll(',', '')),
          personOne: undefined,
          people: arrPe,
          offset: Number(offset),
          order: filtersPlans?.order,
          premiumMax:
            filtersPlans?.premiumValue && filtersPlans.premiumValue > 0
              ? filtersPlans.premiumValue
              : null,
          deductibleMax:
            filtersPlans?.deductibleValue && filtersPlans.deductibleValue > 0
              ? filtersPlans.deductibleValue
              : null,
          planTypes: filtersPlans?.selectedNetworkTypes ?? null,
          issuers: filtersPlans?.selectedCarriers ?? null,
          metalLevels: filtersPlans?.selectedMetalLevels ?? null,
          diseaseMgmtPrograms: filtersPlans?.selectedDiseaseMgmtPrograms ?? null,
          childDentalCoverage: filtersPlans?.childDentalCoverage ?? null,
          adultDentalCoverage: filtersPlans?.adultDentalCoverage ?? null,
          hsaEligible: filtersPlans?.hsaEligible ?? null,
          simpleChoice: filtersPlans?.simpleChoice ?? null,
        },
      })
    } catch (error) {
      console.error('Error in handle Query:', error)
      setErrorData(true)
    }
  }

  useEffect(() => {
    if (!quoteData) return
    const isOk = validateJsonQuote(quoteData)
    if (!isOk) {
      return setErrorData(true)
    }
    const normalizedEnrollment =
      quoteData.personOne.currentEnrollment && quoteData.personOne.currentEnrollment.planId
        ? {...quoteData.personOne.currentEnrollment}
        : null
    const normalizedQuoteData: IForm = {
      ...quoteData,
      year: getYearFromEffectiveDate(quoteData.effectiveDate),
      personOne: {
        ...quoteData.personOne,
        currentEnrollment: normalizedEnrollment,
      },
      people: (quoteData.people ?? []).map((p) => ({...p})),
    }
    setFormData(normalizedQuoteData)
    setArrPeople(
      (normalizedQuoteData.people ?? []).map((p) => ({
        ...p,
        id: p.id ?? Date.now() + Math.random(),
      }))
    )

    if (normalizedEnrollment) {
      setInfoEnrollement({
        id: 1,
        effectiveDate: normalizedEnrollment.effectiveDate,
        planId: normalizedEnrollment.planId,
        usesTobacco: normalizedEnrollment.useTobacco,
      })
    }
    // console.log('quoteData', quoteData)
    const cleandDataQ = removeEmptyKeysDeep(normalizedQuoteData)
    console.log('cleandDataQ', cleandDataQ)
    handleQuery(cleandDataQ)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteData])

  useEffect(() => {
    if (!id && quoteData) return
    if (id && !quoteData)
      try {
        const str = atob(id)
        const obj = JSON.parse(str) as IForm
        const isOk = validateJsonQuote(obj)
        if (!isOk) {
          return setErrorData(true)
        }
        console.log('obj', obj)
        const normalizedEnrollment =
          obj.personOne.currentEnrollment && obj.personOne.currentEnrollment.planId
            ? {...obj.personOne.currentEnrollment}
            : null
        const normalizedObj: IForm = {
          ...obj,
          year: getYearFromEffectiveDate(obj.effectiveDate),
          personOne: {
            ...obj.personOne,
            currentEnrollment: normalizedEnrollment,
          },
          people: (obj.people ?? []).map((p) => ({...p})),
        }
        setFormData((p) => ({...p, ...normalizedObj}))
        setArrPeople(
          (normalizedObj.people ?? []).map((p) => ({
            ...p,
            id: p.id ?? Date.now() + Math.random(),
          }))
        )

        if (normalizedEnrollment) {
          setInfoEnrollement({
            id: 1,
            effectiveDate: normalizedEnrollment.effectiveDate,
            planId: normalizedEnrollment.planId,
            usesTobacco: normalizedEnrollment.useTobacco,
          })
        }

        handleQuery(normalizedObj) // Llamar a la función genérica
      } catch (error) {
        console.error('Error in useEffect:', error)
        setErrorData(true)
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, quoteData])

  useEffect(() => {
    if (error) toast.error('Error: ' + error.message)
  }, [error])

  useEffect(() => {
    if (!data) return

    if (data.marketplace2) {
      const json = JSON.parse(data.marketplace2) as IResultsJson

      if (json.error) {
        toast.error('Error: Not found Quote')
        return
      }

      if (json.validation_errors) {
        toast.error(`Error: ${json.validation_errors[0].error}`)
        return
      }

      setResult(json)

      if (!hasSetInitialRanges.current) {
        // Guardar rangos iniciales
        initialRanges.current = {
          premiums: {...json.ranges.premiums},
          deductibles: {...json.ranges.deductibles},
        }

        // Guardar facetas iniciales
        if (!initialFacets.current) {
          initialFacets.current = json.facet_groups
        }
        setOrderQuery('asc')
        // Establecer filtros iniciales con valores máximos
        setFiltersPlans((prev) => ({
          order: prev?.order ?? 'asc', // asegúrate de mantener el orden si ya estaba
          premiumValue: json.ranges.premiums.max,
          deductibleValue: json.ranges.deductibles.max,
          selectedCarriers: [],
          selectedMetalLevels: [],
          selectedNetworkTypes: [],
          selectedDiseaseMgmtPrograms: [],
        }))

        hasSetInitialRanges.current = true
      }
      setIsUpdatingFilters(false) // Desbloquear la actualización de los filtros
    }
  }, [data])

  const loadMoreQuotes = (page: number, order?: string) => {
    console.log('loadMoreQuotes', order)
    if (order) {
      setOrderQuery(order)
      filtersPlans.order = order
    }
    handleQuery(formData, (page - 1) * SIZE_PAGE_QUICK_QUOTE, filtersPlans)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {name, value, type, checked} = e.target
    if (name === 'effectiveDate') {
      setFormData((prevState) => ({
        ...prevState,
        effectiveDate: value,
        year: getYearFromEffectiveDate(value),
      }))
      return
    }
    setFormData((prevState) => ({
      ...prevState,
      [name]: type === 'checkbox' ? checked : value,
    }))
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    formData.people = arrPeople
    handleQuery(formData) // Llamar a la función genéric
  }

  const createQuote = (plan: any) => {
    console.log('PLAN APLICADO FOR QUOTE', plan)
    setPlanForQuote(plan)
    onOpenQuote()
    // const vals: TFormAllValues = formik.values
    // vals.CMSPlanId = plan.id
    // vals.planName = plan.name
    // vals.metalLevel = getMetalLevelValueByLabel(plan.metal_level)
    // vals.premium = plan.premium
    // vals.taxCredit = plan.aptc_eligible_premium
    // vals.policyTotalCost = plan.premium - plan.aptc_eligible_premium
    // gqlGetOrCreateOperator({variables: {name: plan.issuer.name}})
    // gqlGetCarriers()
    // onCloseDrawer()
    // setFormTabsPolicy('policy-insurance')
  }

  const updatePage = (n: number) => {
    setCurrentPage(n)
  }
  const setRangeFilter = (key: 'premiumValue' | 'deductibleValue', value: number) => {
    setFiltersPlans((prev) => ({
      order: prev?.order ?? 'asc',
      premiumValue: prev?.premiumValue ?? initialRanges.current?.premiums?.max ?? 0,
      deductibleValue: prev?.deductibleValue ?? initialRanges.current?.deductibles?.max ?? 0,
      selectedCarriers: prev?.selectedCarriers ?? [],
      selectedMetalLevels: prev?.selectedMetalLevels ?? [],
      selectedNetworkTypes: prev?.selectedNetworkTypes ?? [],
      selectedDiseaseMgmtPrograms: prev?.selectedDiseaseMgmtPrograms ?? [],
      [key]: value, // sobrescribe premium o deductible según se llame
    }))
  }
  const [filteredGroups, setFilteredGroups] = useState<string[]>([]) // Estado para almacenar múltiples grupos filtrados
  const handleMultiSelectChange = (
    key:
      | 'selectedCarriers'
      | 'selectedMetalLevels'
      | 'selectedNetworkTypes'
      | 'selectedDiseaseMgmtPrograms',
    value: string,
    checked: boolean
  ) => {
    setFiltersPlans((prev) => {
      if (!prev) {
        return {
          order: 'asc',
          [key]: checked ? [value] : [],
        }
      }

      return {
        ...prev,
        [key]: checked
          ? [...(prev[key] ?? []), value]
          : (prev[key] ?? []).filter((v) => v !== value),
      }
    })
  }

  if (!id) {
    return <div>NOT FOUND</div>
  }

  if (errorData) {
    return <div>Error</div>
  }

  if (!result) return <LoadingOneAddQuote currentPage={currentPage} />

  const StarRating = ({rating, onChange}: {rating: number; onChange: (value: number) => void}) => {
    return (
      <div className='star-rating'>
        {[1, 2, 3, 4, 5].map((star) => (
          <i
            key={star}
            className={`bi bi-star${rating >= star ? '-fill' : ''}`}
            style={{
              cursor: 'pointer',
              color: rating >= star ? '#ffc107' : '#e4e5e9',
              fontSize: '1.5rem',
            }}
            onClick={() => onChange(star)}
          ></i>
        ))}
      </div>
    )
  }

  const PlaceholderFilterGroup = ({
    label,
    count,
    minCol,
    maxCol,
  }: {
    label: string
    count: number
    minCol: number
    maxCol: number
  }) => {
    const getRandomColClass = () => {
      const range = maxCol - minCol + 1
      const randomWidth = Math.floor(Math.random() * range) + minCol
      return `col-${randomWidth}`
    }

    return (
      <div className='issuers-list'>
        <label className='form-label'>{label}</label>
        {Array.from({length: count}).map((_, index) => (
          <div key={index} className='form-check'>
            <input type='checkbox' className='form-check-input' disabled />
            <span className='placeholder-glow'>
              <span className={`placeholder ${getRandomColClass()}`} />
            </span>
          </div>
        ))}
      </div>
    )
  }

  return (
    <>
      <div className='one-add-quote-container gap-2'>
        <div className='filter-container'>
          <div className='mt-2 w-100 text-center'>
            <Button
              size='sm'
              variant='info'
              type='button'
              className='btn-filter-plans'
              onClick={() => handleFilters()}
            >
              {isUpdatingFilters && <Spinner animation='grow' size='sm' className='me-2' />}
              {!isUpdatingFilters && <i className='bi bi-funnel'></i>}
              Filter Plan
            </Button>
          </div>
          <div className='filters-by-range'>
            <div className='premium-range'>
              <label htmlFor='premiumRange' className='form-label'>
                Monthly premium max
              </label>
              {filtersPlans?.premiumValue && (
                <div
                  className='premium-value'
                  style={{
                    left: `calc(${
                      ((filtersPlans?.premiumValue - (initialRanges.current?.premiums?.min || 0)) /
                        ((initialRanges.current?.premiums?.max || 1) -
                          (initialRanges.current?.premiums?.min || 0))) *
                      100
                    }% - 10px)`,
                  }}
                >
                  {Math.round(filtersPlans?.premiumValue ?? 0)}
                </div>
              )}

              <input
                type='range'
                className='form-range'
                id='premiumRange'
                min={initialRanges.current?.premiums?.min || 0}
                max={initialRanges.current?.premiums?.max || 0}
                value={filtersPlans?.premiumValue ?? 0}
                onChange={(e) => setRangeFilter('premiumValue', Number(e.target.value))}
              />
            </div>

            {/* Rango para Deductibles */}
            <div className='deductible-range'>
              <label htmlFor='deductibleRange' className='form-label'>
                Deductible max
              </label>
              {filtersPlans?.deductibleValue && (
                <div
                  className='deductible-value'
                  style={{
                    left: `calc(${
                      ((filtersPlans.deductibleValue -
                        (initialRanges.current?.deductibles?.min || 0)) /
                        ((initialRanges.current?.deductibles?.max || 1) -
                          (initialRanges.current?.deductibles?.min || 0))) *
                      100
                    }% - 10px)`,
                  }}
                >
                  {Math.round(filtersPlans?.deductibleValue ?? 0)}
                </div>
              )}
              <input
                type='range'
                className='form-range'
                id='deductibleRange'
                min={initialRanges.current?.deductibles?.min || 0}
                max={initialRanges.current?.deductibles?.max || 0}
                value={filtersPlans?.deductibleValue ?? 0}
                onChange={(e) => setRangeFilter('deductibleValue', Number(e.target.value))}
              />
            </div>
            {/* <div className='rating-filter'>
              <label htmlFor='ratingRange' className='form-label'>
                Maximun Rating
              </label>
              <StarRating
                rating={filtersPlans.rating || 0}
                onChange={(value) =>
                  setFiltersPlans((prev) => ({
                    ...prev,
                    rating: value,
                  }))
                }
              />
            </div> */}
          </div>

          <div className='filters-by-checks'>
            {isUpdatingFilters ? (
              <>
                <PlaceholderFilterGroup label='Carriers' count={5} minCol={6} maxCol={12} />
                <PlaceholderFilterGroup label='Metal Levels' count={4} minCol={5} maxCol={10} />
                <PlaceholderFilterGroup label='Networks' count={4} minCol={5} maxCol={10} />
                <PlaceholderFilterGroup
                  label='Disease Management Programs'
                  count={4}
                  minCol={4}
                  maxCol={9}
                />
                <PlaceholderFilterGroup label='Plan Features' count={4} minCol={3} maxCol={8} />
              </>
            ) : (
              <>
                {['issuers', 'metalLevels', 'types', 'diseaseMgmtPrograms'].map((groupName) => (
                  <div key={groupName} className={`${groupName}-filter`}>
                    <label className='form-label'>
                      {groupName === 'issuers'
                        ? 'Carriers'
                        : groupName === 'metalLevels'
                        ? 'Metal Levels'
                        : groupName === 'types'
                        ? 'Networks'
                        : 'Disease Management Programs'}
                    </label>
                    <div className={`${groupName}-list`}>
                      {(filteredGroups.includes(groupName)
                        ? initialFacets.current?.find((group) => group.name === groupName)
                        : result.facet_groups.find((group) => group.name === groupName)
                      )?.facets.map((facet, index) => (
                        <div key={index} className='form-check'>
                          <input
                            type='checkbox'
                            className='form-check-input'
                            id={`${groupName}-${index}`}
                            value={facet.value}
                            checked={
                              filtersPlans[
                                groupName === 'issuers'
                                  ? 'selectedCarriers'
                                  : groupName === 'metalLevels'
                                  ? 'selectedMetalLevels'
                                  : groupName === 'types'
                                  ? 'selectedNetworkTypes'
                                  : 'selectedDiseaseMgmtPrograms'
                              ]?.includes(facet.value) || false
                            }
                            onChange={(e) =>
                              handleMultiSelectChange(
                                groupName === 'issuers'
                                  ? 'selectedCarriers'
                                  : groupName === 'metalLevels'
                                  ? 'selectedMetalLevels'
                                  : groupName === 'types'
                                  ? 'selectedNetworkTypes'
                                  : 'selectedDiseaseMgmtPrograms',
                                e.target.value,
                                e.target.checked
                              )
                            }
                          />
                          <label className='form-check-label' htmlFor={`${groupName}-${index}`}>
                            {facet.value} ({facet.count})
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                <div className='plan-features-list'>
                  <label className='form-label'>Plan Features</label>
                  <div className='form-check'>
                    <input
                      type='checkbox'
                      className='form-check-input'
                      id='childDentalCoverage'
                      checked={filtersPlans.childDentalCoverage || false}
                      onChange={(e) =>
                        setFiltersPlans((prev) => ({
                          ...prev,
                          childDentalCoverage: e.target.checked,
                        }))
                      }
                    />
                    <label className='form-check-label' htmlFor='childDentalCoverage'>
                      Child Dental Coverage
                    </label>
                  </div>
                  <div className='form-check'>
                    <input
                      type='checkbox'
                      className='form-check-input'
                      id='adultDentalCoverage'
                      checked={filtersPlans.adultDentalCoverage || false}
                      onChange={(e) =>
                        setFiltersPlans((prev) => ({
                          ...prev,
                          adultDentalCoverage: e.target.checked,
                        }))
                      }
                    />
                    <label className='form-check-label' htmlFor='adultDentalCoverage'>
                      Adult Dental Coverage
                    </label>
                  </div>
                  <div className='form-check'>
                    <input
                      type='checkbox'
                      className='form-check-input'
                      id='hsaEligible'
                      checked={filtersPlans.hsaEligible || false}
                      onChange={(e) =>
                        setFiltersPlans((prev) => ({
                          ...prev,
                          hsaEligible: e.target.checked,
                        }))
                      }
                    />
                    <label className='form-check-label' htmlFor='hsaEligible'>
                      HSA Eligible
                    </label>
                  </div>
                  <div className='form-check'>
                    <input
                      type='checkbox'
                      className='form-check-input'
                      id='simpleChoice'
                      checked={filtersPlans.simpleChoice || false}
                      onChange={(e) =>
                        setFiltersPlans((prev) => ({
                          ...prev,
                          simpleChoice: e.target.checked,
                        }))
                      }
                    />
                    <label className='form-check-label' htmlFor='simpleChoice'>
                      Simple Choice
                    </label>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        <div className='result-filter-container'>
          <AccordionForm
            handleSubmit={handleSubmit}
            handleChange={handleChange}
            setFormData={setFormData}
            setArrPeople={setArrPeople}
            setInfoEnrollement={setInfoEnrollement}
            formData={formData}
            arrPeople={arrPeople}
            infoEnrollement={infoEnrollement}
            hasSetInitialRanges={hasSetInitialRanges}
          />
          {loadingPlans && <LoadingOneAddQuote currentPage={currentPage} />}
          {!loadingPlans && result && (
            <ResultsCards
              queryData={formData}
              loadMoreQuotes={loadMoreQuotes}
              updatePage={updatePage}
              result={result}
              orderQuery={orderQuery}
              currentPage={currentPage}
              onSelectPlan={onSelectPlan ?? createQuote}
            />
          )}
        </div>
      </div>

      {isOpenQuote && planForQuote && (
        <ModalAddPolice
          refetch={() => {}}
          isOpen={isOpenQuote}
          onClose={onCloseQuote}
          keyboard={false}
          backdrop='static'
          isQuote={true}
          planSelectPrev={planForQuote}
        />
      )}
    </>
  )
}
